import { useState, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UseMobileVoiceFallbackReturn {
  isRecording: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  transcript: string;
  lastResponse: string;
  startRecording: () => void;
  stopRecording: () => void;
  error: string | null;
}

export const useMobileVoiceFallback = (positId: string): UseMobileVoiceFallbackReturn => {
  const { user } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [lastResponse, setLastResponse] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/ogg'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return 'audio/webm'; // fallback
  };

  const processAudio = async (audioBlob: Blob) => {
    if (!user) {
      setError('Please log in to use voice recording');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Convert to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      
      const base64Audio = await base64Promise;

      // Use mobile-specific endpoint if available
      const endpoint = 'mobile-voice-to-text';


      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('Session expired - please log in again');
      }

      // Transcribe audio
      const { data: transcriptionData, error: transcriptionError } = await supabase.functions.invoke(endpoint, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`,
        },
        body: { 
          audio: base64Audio,
          userId: user.id
        }
      });

      if (transcriptionError) {
        throw new Error(`Transcription failed: ${transcriptionError.message}`);
      }

      const transcribedText = transcriptionData?.text || transcriptionData?.transcript || '';
      if (!transcribedText.trim()) {
        throw new Error('No speech detected. Please try speaking more clearly.');
      }

      // Clean up transcript for common misheard words
      let cleanedTranscript = transcribedText;
      
      // Fix common misheard variations of "posit" - replace with the correct word
      cleanedTranscript = cleanedTranscript.replace(/\b(closet|deposit|pause it|positive|pos it)\b/gi, 'posit');
      
      // Ensure "posit" is always capitalized when referring to the knowledge base
      cleanedTranscript = cleanedTranscript.replace(/\bposit\b/gi, 'Posit');

      setTranscript(cleanedTranscript);
      if (cleanedTranscript !== transcribedText) {
      }

      // Process with AI
      const { data: chatData, error: chatError } = await supabase.functions.invoke('chat-with-posit-v4-unified', {
        body: {
          message: cleanedTranscript,
          posit_id: positId,
          user_id: user.id,
          conversation_id: null,
          voiceMode: true
        }
      });

      if (chatError) throw chatError;

      const aiResponse = chatData.content || chatData.message || "I'm sorry, I couldn't generate a response.";
      setLastResponse(aiResponse);

      // Convert to speech using OpenAI TTS
      await playAIResponse(aiResponse);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process audio');
    } finally {
      setIsProcessing(false);
    }
  };

  const playAIResponse = async (text: string) => {
    try {
      setIsSpeaking(true);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-tts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: text,
          voice: 'alloy',
          model: 'tts-1', // Using faster model
          response_format: 'mp3',
          speed: 1.1 // 10% faster speech - more natural
        })
      });

      if (!response.ok) {
        throw new Error(`TTS failed: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      audioRef.current = new Audio(audioUrl);
      
      audioRef.current.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };

      audioRef.current.onerror = () => {
        setIsSpeaking(false);
        setError('Audio playback failed');
        URL.revokeObjectURL(audioUrl);
      };

      await audioRef.current.play();

    } catch (err) {
      setIsSpeaking(false);
      setError('Failed to play audio response');
    }
  };

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1
        }
      });

      const mimeType = getSupportedMimeType();
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        
        // Stop all audio tracks
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size > 0) {
          processAudio(audioBlob);
        } else {
          setError('No audio recorded. Please try again.');
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setTranscript('');

    } catch (err) {
      
      if (err.name === 'NotAllowedError') {
        setError('Microphone permission denied. Please allow microphone access.');
      } else {
        setError('Failed to start recording. Please try again.');
      }
    }
  }, [positId, user]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  return {
    isRecording,
    isProcessing,
    isSpeaking,
    transcript,
    lastResponse,
    startRecording,
    stopRecording,
    error
  };
};